import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setColonyAuthToken } from "@/lib/colonyApi";
import { api, formatErr, setAuthToken } from "@/lib/api";
import { useColonyAuth } from "@/contexts/ColonyAuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Home } from "lucide-react";
import { useColonyBrand } from "./useColonyBrand";
import { portalDestination, takePendingManagementDeepLink } from "@/lib/managementDeepLink";

/**
 * Unified Google OAuth callback for the /managementusa entry point.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Posts the session_id to `POST /api/managementusa/auth/google-callback`,
 * which resolves the Google email against BOTH stores and tells us which
 * portal the person belongs to:
 *   - kind "colony" → colony session → /colonies
 *   - kind "worker" → pool session   → /worker
 *   - kind "mgmt"   → pool session   → /managementusa/admin
 * There's no auto-signup — the office must add the person first.
 */
export default function ColonyAuthCallback() {
  useColonyBrand();
  const navigate = useNavigate();
  const { refresh } = useColonyAuth();
  const { refresh: refreshPool } = useAuth();
  const hasProcessed = useRef(false);
  const deepLinkRef = useRef(takePendingManagementDeepLink());
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setError("Missing session_id — please try signing in again.");
      return;
    }

    const run = async () => {
      try {
        const { data } = await api.post(
          "/managementusa/auth/google-callback",
          { session_id: sessionId },
        );
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome${data?.user?.name ? `, ${data.user.name}` : ""}!`);
        if (data?.kind === "colony") {
          if (data?.token) setColonyAuthToken(data.token);
          await refresh();
          navigate(portalDestination("/colonies", deepLinkRef.current.next, deepLinkRef.current.ticket), { replace: true });
        } else {
          if (data?.token) setAuthToken(data.token);
          await refreshPool();
          const home = data?.kind === "mgmt" ? "/managementusa/admin" : "/worker";
          navigate(portalDestination(home, deepLinkRef.current.next, deepLinkRef.current.ticket), { replace: true });
        }
      } catch (e) {
        setError(
          formatErr(e.response?.data?.detail) ||
          e.message ||
          "Google sign-in failed."
        );
      }
    };
    run();
  }, [navigate, refresh, refreshPool]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-emerald-50 px-6">
      <div className="text-center max-w-sm" data-testid="colony-auth-callback-screen">
        <Home className="w-8 h-8 mx-auto text-emerald-700 mb-3" />
        {error ? (
          <>
            <p className="overline text-destructive mb-2">Sign-in failed</p>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <button
              type="button"
              className="text-sm underline text-emerald-800"
              onClick={() => navigate("/managementusa", { replace: true })}
              data-testid="colony-auth-callback-back"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <p className="overline mb-1">Finishing sign-in</p>
            <p className="text-sm text-muted-foreground">Linking your Google account…</p>
          </>
        )}
      </div>
    </div>
  );
}
